/**
 * Which organizations hold a capability.
 *
 * Careforce holds every capability, so a scope always keeps it. The rest is a
 * choice between one product-wide row serving everybody and a row per
 * organization — a distinction the column has to preserve, because it decides
 * what an organization added later gets by default.
 */

import { PRODUCT_WIDE_SCOPE } from './grants'

/** The organization that holds every capability whatever the column says. */
export const CAREFORCE_SLUG = 'careforce'

/** Whether one product-wide row serves every organization. */
export function isEveryOrg(orgs: string[]): boolean {
  return orgs.includes(PRODUCT_WIDE_SCOPE)
}

/**
 * The scope after turning one entry on or off.
 *
 * "All orgs" is a master switch over reach: on gives every organization the
 * capability, off takes it back to Careforce, which holds everything
 * regardless. It reads what the scope reaches, so ticking every organization
 * one at a time arrives at the same ticked switch as choosing it outright.
 *
 * Reach is not shape. A scope covering everybody by a row each has no
 * product-wide row, and no single click makes one — the switch is already on,
 * so consolidating takes turning it off and on again. What that costs is only
 * felt by an organization added later, which a product-wide row would have
 * covered and a list of rows written before it does not.
 *
 * A single org is not the same switch. Turning one off while product-wide
 * means "everyone except this one", so the scope expands into the explicit
 * list and drops that one — the row cannot say it any other way once there is
 * no `*` row.
 */
export function toggleOrgScope(
  orgs: string[],
  slug: string,
  allSlugs: string[]
): string[] {
  if (slug === PRODUCT_WIDE_SCOPE) {
    return coversEveryOrg(orgs, allSlugs)
      ? [CAREFORCE_SLUG]
      : [PRODUCT_WIDE_SCOPE]
  }

  const expanded = isEveryOrg(orgs)
    ? [...allSlugs]
    : orgs.includes(CAREFORCE_SLUG)
      ? orgs
      : [CAREFORCE_SLUG, ...orgs]

  if (!expanded.includes(slug)) {
    const added = [...expanded, slug]
    // Landing on every organization stores the product-wide row rather than a
    // row each. Both reach everybody, but only the shared row keeps reaching
    // them: a row each hands every org its own copy of the roles, which then
    // stops following the column that appears to govern it.
    return coversEveryOrg(added, allSlugs) ? [PRODUCT_WIDE_SCOPE] : added
  }
  // Careforce cannot be turned off; it holds everything by definition.
  if (slug === CAREFORCE_SLUG) return expanded
  return expanded.filter((entry) => entry !== slug)
}

/**
 * Every organization the control lists.
 *
 * The org list comes from Config Manager, but a capability can be held by an
 * organization that has no configuration row — and one that is not listed
 * cannot be unticked. Anything already in the scope is listed too.
 */
export function listedOrgs(allSlugs: string[], scope: string[]): string[] {
  const slugs = new Set(allSlugs)
  for (const slug of scope) {
    if (slug !== PRODUCT_WIDE_SCOPE) slugs.add(slug)
  }
  slugs.add(CAREFORCE_SLUG)
  return [
    CAREFORCE_SLUG,
    ...[...slugs].filter((slug) => slug !== CAREFORCE_SLUG).toSorted(),
  ]
}

/**
 * Whether the scope reaches every listed organization.
 *
 * Not the same as `isEveryOrg`, and deliberately so: one product-wide row
 * covers organizations added later, while a row each covers only the ones
 * that exist now. The control has to show that the two currently agree
 * without claiming they are the same thing.
 */
export function coversEveryOrg(scope: string[], allSlugs: string[]): boolean {
  if (isEveryOrg(scope)) return true
  // Careforce is in every scope by definition, so it cannot be evidence of
  // reaching everybody. A list naming no customer says nothing either way —
  // which is what an unreadable registry looks like — and "nothing is known"
  // must not read as "everything is covered".
  const customers = allSlugs.filter((slug) => slug !== CAREFORCE_SLUG)
  if (customers.length === 0) return false
  const held = new Set(scope)
  return customers.every((slug) => held.has(slug))
}

/**
 * Short summary for the closed control.
 *
 * Kept terse because the control sits in a fixed-width column: the header
 * already says these are orgs, so the count does not repeat the word.
 *
 * Reads "All orgs" whenever the scope reaches everybody, counting a row each
 * as well as one product-wide row: the summary has to agree with the switch
 * it sits above, and counting up to the number of orgs there are says the
 * same thing at more length.
 */
export function orgScopeLabel(orgs: string[], allSlugs: string[] = []): string {
  if (coversEveryOrg(orgs, allSlugs)) return 'All orgs'
  const customers = orgs.filter((slug) => slug !== CAREFORCE_SLUG).length
  // No row at all still means Careforce: it holds every capability whatever
  // the column says, so there is no scope this control can call empty.
  if (customers === 0) return 'Careforce only'
  return `Careforce +${customers}`
}
